import type { Theme, SxProps } from '@mui/material/styles';

import React from 'react';
import { Icon as Iconify } from '@iconify/react';

import { Box } from '@mui/material';

/**
 * True if `icon` should be rendered as an image (URL) rather than an Iconify
 * name. Iconify names always contain a colon (e.g. `mdi:apple`), URLs never do
 * unless they include a scheme — which we detect explicitly.
 */
export function iconIsUrl(icon: string): boolean {
  return /^(https?:)?\/\//.test(icon) || icon.startsWith('/') || icon.startsWith('data:');
}

/**
 * Normalises an Iconify string. The backend prompt-documents `icon` as an
 * "iconify name", so the LLM almost always emits prefixed strings like
 * `mdi:apple` or `solar:list-bold`. Occasionally it drifts and produces a bare
 * name (e.g. `apple`) — we assume MDI in that case, which covers most cases.
 */
export function normalizeIconifyName(icon: string): string {
  if (icon.includes(':')) return icon;
  return `mdi:${icon}`;
}

interface RenderIconProps {
  icon: string | null | undefined;
  size: number;
  sx?: SxProps<Theme>;
  /**
   * Called when an image URL fails to load. Lets callers hide their tile so
   * a broken `<img>` placeholder never sits next to the title. Iconify names
   * are pre-gated by {@link isRenderableIconName}, so this only fires for
   * URL/data icons.
   */
  onError?: () => void;
}

/**
 * Renders either an Iconify icon or an `<img>` depending on whether the
 * incoming string looks like a URL. The field is prompt-documented as an
 * Iconify name so URLs are rare LLM drift — but handling both keeps the
 * renderer resilient.
 */
export function RenderIcon({ icon, size, sx, onError }: RenderIconProps): React.JSX.Element | null {
  if (!icon) return null;

  if (iconIsUrl(icon)) {
    return (
      <Box
        component="img"
        src={icon}
        alt=""
        onError={onError}
        sx={[
          { width: size, height: size, objectFit: 'contain', display: 'block' },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
    );
  }

  return (
    <Box
      component={Iconify}
      icon={normalizeIconifyName(icon)}
      sx={[
        { width: size, height: size, flexShrink: 0, display: 'inline-flex' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}
